import { Chip } from '@heroui/chip';
import { Link, useNavigate } from 'react-router-dom';
import cn from 'classnames';
import { CatalogueShortType } from '@/views/CatalogueView';
import { AppImage } from '@/shared/ui/AppImage';
import { Svg } from '@/shared/ui/Svg';
import StarIcon from '@/shared/assets/icons/filled-star.svg';
import CardPreview from '@/shared/assets/images/catalog-card-preview.png';
import { UserAvatar } from '@/shared/ui/UserAvatar';
import { dictionaryLookup } from '@/shared/lib/utils/dictionary';
import { COURSE_TYPE } from '@/shared/constants/courseData';
import { formatPrice } from '@/shared/lib/utils/formatPrice';

interface CourseCardProps {
  course: CatalogueShortType;
}

export const CourseCard = ({ course }: CourseCardProps) => {
  const { id, image, title, type, rate, price, leadTeacher } = course;

  const courseType = dictionaryLookup(type, COURSE_TYPE);
  const formattedPrice = price ? formatPrice(price, 'GBP') : null;
  const stars = rate ? Array.from({ length: rate }, (_, idx) => `star-${id}-${idx}`) : [];

  const navigate = useNavigate();

  const handleToNavigate = () => {
    navigate(`/catalogue/course/${id}`);
  };
  return (
    // Щелчок по всей карточке оставляем как был. В табуляцию карточку целиком
    // не ставим: внутри уже есть ссылка на преподавателя, а вложенный интерактив
    // внутри кнопки — нарушение. С клавиатуры курс открывается ссылкой на заголовке.
    <div
      role="presentation"
      onClick={handleToNavigate}
      className="w-full min-w-[min(346px,100%)] h-full p-4 rounded-[12px] bg-white flex flex-col gap-y-3 transition-all duration-300
      cursor-pointer hover:shadow-md"
    >
      {/* Название курса стоит заголовком в той же карточке, дублировать его в alt незачем */}
      <AppImage
        src={image || CardPreview}
        alt=""
        className="w-full h-[282px] rounded-[14px] overflow-hidden object-cover"
      />
      <div className="flex flex-col gap-y-2">
        <Chip
          className={cn('text-white bg-red', {
            ['bg-[#211D55]']: type === 'short',
          })}
        >
          {courseType}
        </Chip>
        <h2 className="text-lg font-bold">
          <Link
            to={`/catalogue/course/${id}`}
            onClick={(event) => event.stopPropagation()}
            className="hover:underline"
          >
            {title}
          </Link>
        </h2>
        {stars.length > 0 && (
          <div className="flex items-center gap-x-1">
            {stars.map((starKey) => (
              <Svg Icon={StarIcon} key={starKey} className="text-yellow-tertiary" />
            ))}
          </div>
        )}
        {leadTeacher ? (
          <UserAvatar
            avatar={leadTeacher.avatar}
            userName={`${leadTeacher.firstName} ${leadTeacher.lastName}`}
            href={`/staff/${leadTeacher.id}`}
          />
        ) : (
          <span className="min-h-6">Course lead teacher not assigned</span>
        )}
      </div>
      <p className="text-xl font-bold">{formattedPrice}</p>
    </div>
  );
};
